/**
 * General table schema for pdfme
 * Supports custom cell renderers for special content (e.g., profile cross-section previews)
 */

// =============================================================================
// TYPES
// =============================================================================

export enum Align {
  Left = "left",
  Center = "center",
  Right = "right",
}

export interface TableColumn {
  name: string;
  widthPct: number;
  align: Align;
  renderer?: (
    value: unknown,
    cellX: number,
    cellY: number,
    cellWidth: number,
    cellHeight: number,
    context: CellRenderContext,
  ) => void | Promise<void>;
  rendererType?: string; // Identifier for renderer (for serialization)
}

// Registry for cell renderers (functions can't be serialized)
const rendererRegistry = new Map<
  string,
  (
    value: unknown,
    cellX: number,
    cellY: number,
    cellWidth: number,
    cellHeight: number,
    context: CellRenderContext,
  ) => void | Promise<void>
>();

/**
 * Register a cell renderer function
 */
export function registerCellRenderer(
  type: string,
  renderer: (
    value: unknown,
    cellX: number,
    cellY: number,
    cellWidth: number,
    cellHeight: number,
    context: CellRenderContext,
  ) => void | Promise<void>,
): void {
  rendererRegistry.set(type, renderer);
}

export interface CellRenderContext {
  page: import("pdf-lib").PDFPage;
  font: import("pdf-lib").PDFFont;
  fontBold: import("pdf-lib").PDFFont;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  rowIndex: number;
  rowData: unknown[];
  fontSize?: number; // Font size in points for body text
}

export interface TableData {
  columns: TableColumn[];
  rows: unknown[][];
  rowHeight?: number; // in mm, default 7
  fontSize?: {
    header?: number;
    body?: number;
  };
  showHeader?: boolean; // Whether to show the header row, default true
}

// =============================================================================
// CONSTANTS
// =============================================================================

// Import central font size definitions
import { PDF_TEXT_SIZE, PDF_TITLE_SIZE } from "./template";

const DEFAULT_FONT_SIZE = {
  HEADER: PDF_TITLE_SIZE, // Use central font size definition
  BODY: PDF_TEXT_SIZE, // Use central font size definition
};

const COLOR = {
  BLACK: { r: 0, g: 0, b: 0 },
  GRAY: { r: 0.4, g: 0.4, b: 0.4 },
  LIGHT_GRAY: { r: 0.96, g: 0.96, b: 0.96 },
  BORDER: { r: 0.4, g: 0.4, b: 0.4 },
};

const MM_TO_PT = 2.83465;
const DEFAULT_ROW_HEIGHT_MM = 7;

// =============================================================================
// TABLE RENDERING
// =============================================================================

/**
 * Parse and validate table data from JSON string
 */
function parseTableData(value: string): TableData | null {
  try {
    if (!value || typeof value !== "string") {
      return null;
    }
    const tableData = JSON.parse(value);
    if (!tableData.columns || !tableData.rows || tableData.rows.length === 0) {
      return null;
    }
    return tableData;
  } catch (e) {
    console.warn("Failed to parse table data:", e);
    return null;
  }
}

/**
 * Restore renderer functions from registry (they were lost during JSON serialization)
 */
function restoreColumnRenderers(columns: TableColumn[]): TableColumn[] {
  return columns.map((col) => {
    if (!col.rendererType || col.renderer) {
      return col;
    }
    const renderer = rendererRegistry.get(col.rendererType);
    if (renderer) {
      return { ...col, renderer };
    }
    console.warn(
      `Renderer type "${col.rendererType}" not found in registry. Available types:`,
      Array.from(rendererRegistry.keys()),
    );
    return col;
  });
}

/**
 * Check if a value can be safely converted to string
 */
function isStringifiable(value: unknown): value is string | number | boolean {
  return (
    value != null &&
    typeof value !== "object" &&
    typeof value !== "function" &&
    typeof value !== "symbol"
  );
}

/**
 * Calculate text X position based on alignment
 */
function calculateTextX(
  cellX: number,
  cellWidth: number,
  textWidth: number,
  align: Align,
): number {
  if (align === Align.Center) {
    return cellX + (cellWidth - textWidth) / 2;
  }
  if (align === Align.Right) {
    return cellX + cellWidth - textWidth - 2;
  }
  return cellX + 2; // Default padding left
}

/**
 * Draw a dashed line below a row
 */
function drawDashedLine(
  page: import("pdf-lib").PDFPage,
  startX: number,
  y: number,
  tableWidth: number,
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB,
): void {
  page.drawLine({
    start: { x: startX, y },
    end: { x: startX + tableWidth, y },
    thickness: 0.3,
    color: rgb(COLOR.BORDER.r, COLOR.BORDER.g, COLOR.BORDER.b),
    dashArray: [2, 2], // Dashed line: 2pt dash, 2pt gap
  });
}

interface DrawCellParams {
  cellValue: unknown;
  cellX: number;
  cellY: number;
  cellWidth: number;
  rowHeight: number;
  col: TableColumn;
  fontSize: number;
  font: import("pdf-lib").PDFFont;
  fontBold: import("pdf-lib").PDFFont;
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  rowIndex: number;
  rowData: unknown[];
}

/**
 * Draw a single cell (either with custom renderer or default text)
 */
async function drawCell(params: DrawCellParams): Promise<void> {
  const {
    cellValue,
    cellX,
    cellY,
    cellWidth,
    rowHeight,
    col,
    fontSize,
    font,
    fontBold,
    page,
    rgb,
    rowIndex,
    rowData,
  } = params;

  if (col.renderer) {
    const context: CellRenderContext = {
      page,
      font,
      fontBold,
      rgb,
      rowIndex,
      rowData,
      fontSize,
    };
    await col.renderer(cellValue, cellX, cellY, cellWidth, rowHeight, context);
    return;
  }

  // Default text rendering
  const text = isStringifiable(cellValue) ? String(cellValue) : "";
  const textWidth = font.widthOfTextAtSize(text, fontSize);
  const textX = calculateTextX(cellX, cellWidth, textWidth, col.align);

  page.drawText(text, {
    x: textX,
    y: cellY - rowHeight / 2 - fontSize / 2 + 1,
    size: fontSize,
    font,
    color: rgb(COLOR.BLACK.r, COLOR.BLACK.g, COLOR.BLACK.b),
  });
}

interface DrawHeaderParams {
  columns: TableColumn[];
  startX: number;
  startY: number;
  tableWidth: number;
  rowHeight: number;
  fontSize: number;
  fontBold: import("pdf-lib").PDFFont;
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
}

/**
 * Draw the table header
 */
function drawHeader(params: DrawHeaderParams): number {
  const {
    columns,
    startX,
    startY,
    tableWidth,
    rowHeight,
    fontSize,
    fontBold,
    page,
    rgb,
  } = params;

  let currentX = startX;
  const currentY = startY;

  columns.forEach((col) => {
    const colWidth = tableWidth * col.widthPct;
    const textWidth = fontBold.widthOfTextAtSize(col.name, fontSize);
    const textX = calculateTextX(currentX, colWidth, textWidth, col.align);

    page.drawText(col.name, {
      x: textX,
      y: currentY - rowHeight / 2 - fontSize / 2 + 1,
      size: fontSize,
      font: fontBold,
      color: rgb(COLOR.BLACK.r, COLOR.BLACK.g, COLOR.BLACK.b),
    });

    currentX += colWidth;
  });

  drawDashedLine(page, startX, currentY - rowHeight, tableWidth, rgb);
  return currentY - rowHeight;
}

interface DrawRowParams {
  row: unknown[];
  rowIndex: number;
  columns: TableColumn[];
  startX: number;
  currentY: number;
  tableWidth: number;
  rowHeight: number;
  fontSize: number;
  font: import("pdf-lib").PDFFont;
  fontBold: import("pdf-lib").PDFFont;
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  totalRows: number;
}

/**
 * Draw a single table row
 */
async function drawRow(params: DrawRowParams): Promise<number> {
  const {
    row,
    rowIndex,
    columns,
    startX,
    currentY,
    tableWidth,
    rowHeight,
    fontSize,
    font,
    fontBold,
    page,
    rgb,
    totalRows,
  } = params;

  let currentX = startX;

  for (const [colIndex, col] of columns.entries()) {
    const colWidth = tableWidth * col.widthPct;
    const cellValue = row[colIndex];

    await drawCell({
      cellValue,
      cellX: currentX,
      cellY: currentY,
      cellWidth: colWidth,
      rowHeight,
      col,
      fontSize,
      font,
      fontBold,
      page,
      rgb,
      rowIndex,
      rowData: row,
    });

    currentX += colWidth;
  }

  if (rowIndex < totalRows - 1) {
    drawDashedLine(page, startX, currentY - rowHeight, tableWidth, rgb);
  }

  return currentY - rowHeight;
}

/**
 * Draw a general table with configurable columns and custom cell renderers
 */
export async function drawTable(
  arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  },
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB,
  StandardFonts: typeof import("pdf-lib").StandardFonts,
): Promise<void> {
  const { pdfDoc, page, schema, value } = arg;
  const { x, y } = schema.position;
  const { width } = schema;

  const tableData = parseTableData(value);
  if (!tableData) {
    return;
  }

  const columns = restoreColumnRenderers(tableData.columns);

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // PDF coordinates setup
  const pageHeight = page.getHeight();
  const startX = x * MM_TO_PT;
  const startY = pageHeight - y * MM_TO_PT;

  const tableWidth = width * MM_TO_PT;
  const rowHeight = (tableData.rowHeight ?? DEFAULT_ROW_HEIGHT_MM) * MM_TO_PT;
  const fontSize = {
    header: tableData.fontSize?.header ?? DEFAULT_FONT_SIZE.HEADER,
    body: tableData.fontSize?.body ?? DEFAULT_FONT_SIZE.BODY,
  };

  const { rows } = tableData;
  const showHeader = tableData.showHeader !== false; // Default to true

  let currentY = startY;

  if (showHeader) {
    currentY = drawHeader({
      columns,
      startX,
      startY: currentY,
      tableWidth,
      rowHeight,
      fontSize: fontSize.header,
      fontBold,
      page,
      rgb,
    });
  }

  for (const [rowIndex, row] of rows.entries()) {
    currentY = await drawRow({
      row,
      rowIndex,
      columns,
      startX,
      currentY,
      tableWidth,
      rowHeight,
      fontSize: fontSize.body,
      font,
      fontBold,
      page,
      rgb,
      totalRows: rows.length,
    });
  }
}

// =============================================================================
// SCHEMA DEFINITION
// =============================================================================

export const tableSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "#f5f5f5";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.fontSize = "10px";
    div.style.color = "#666";
    div.textContent = "Table Preview";
    arg.rootElement.appendChild(div);
  },

  pdf: async (arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  }) => {
    const { rgb, StandardFonts } = await import("pdf-lib");
    await drawTable(arg, rgb, StandardFonts);
  },

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "customTable",
      type: "customTable",
      position: { x: 0, y: 0 },
      width: 190,
      height: 60,
    },
    defaultValue: JSON.stringify({
      columns: [],
      rows: [],
    }),
  },
};

export default tableSchema;
